// navigation/app-nav.tsx
// The persistent left navigation rail. Expanded (288px) + collapsed icon rail
// (80px), dark-mode aware, keyboard + a11y, "/" command palette, and the
// per-feature tri-state model: "live" (normal, navigable), "locked" (visible
// with a lock icon, opens an upsell on click), and "hidden" (absent from the
// rail and palette). Replaces the old Navigation; preserves the same prop
// contract so the App.tsx mount is unchanged.
import { useEffect, useRef, useState } from "react";
import {
  Search, Plus, ChevronDown, ChevronRight, ChevronsLeft, ChevronsRight,
  Sun, Moon, Settings, LogOut, Lock, ArrowRight, Menu, X, Check,
} from "lucide-react";
import { useTheme } from "@/app/contexts/theme-context";
import { useResponsive } from "@/app/hooks/common/use-breakpoint";
import {
  FEATURES, SECTIONS, MANAGE_ITEMS, featureForPage, type FeatureId, type NavFeature,
} from "./nav-config";
import { useEntitlements, stateOf } from "./use-entitlements";
import { CommandPalette } from "./command-palette";
import { LockedFeatureModal } from "./locked-feature-modal";
import "./app-nav.css";

const NAV_COLLAPSED_KEY = "navigation-is-collapsed";

function storedCollapsed(): boolean {
  try { return localStorage.getItem(NAV_COLLAPSED_KEY) === "true"; } catch { return false; }
}
function brandInitials(name?: string): string {
  const w = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!w.length) return "U";
  if (w.length === 1) return w[0].slice(0, 2).toUpperCase();
  return (w[0][0] + w[1][0]).toUpperCase();
}
// Single-letter brand mark (people avatars still use brandInitials).
function brandMonogram(name?: string): string {
  const c = (name || "").trim()[0];
  return c ? c.toUpperCase() : "U";
}
// Brand workspace chip: a single letter on a teal gradient so it reads as an
// intentional mark, not truncated initials. Teal (not violet) keeps the
// "violet = AI" reservation intact.
const BRAND_CHIP = "linear-gradient(135deg, #2DD4CE 0%, #0B8B86 100%)";

export interface AppNavProps {
  currentPage: string;
  onNavigate: (page: string) => void;
  onCollapsedChange?: (isCollapsed: boolean) => void;
  onOpenCommandPalette?: () => void;
  onLogout: () => void;
  activeBrandName?: string;
  forcedCollapsed?: boolean;
  onOpenBrandSwitcher?: () => void;
  onOpenRunHistory?: () => void;
  runCount?: number;
}

export function AppNav(props: AppNavProps) {
  const {
    currentPage, onNavigate, onCollapsedChange, onLogout,
    activeBrandName, forcedCollapsed, onOpenBrandSwitcher, runCount = 0,
  } = props;

  const { theme, setTheme } = useTheme();
  const { isMobile } = useResponsive();
  const { states, user } = useEntitlements();

  const [collapsed, setCollapsed] = useState<boolean>(storedCollapsed);
  const [menuOpen, setMenuOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [advOpen, setAdvOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [lockedModal, setLockedModal] = useState<FeatureId | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [tip, setTip] = useState<{ show: boolean; text: string; top: number; left: number }>({ show: false, text: "", top: 0, left: 0 });

  const tt = theme === "dark" ? "#2DD4CE" : "#04938F";
  const effectiveCollapsed = isMobile ? false : collapsed;

  // Persist + report collapse.
  useEffect(() => {
    try { localStorage.setItem(NAV_COLLAPSED_KEY, String(collapsed)); } catch { /* ignore */ }
    onCollapsedChange?.(collapsed);
  }, [collapsed, onCollapsedChange]);

  useEffect(() => {
    if (typeof forcedCollapsed === "boolean") setCollapsed(forcedCollapsed);
  }, [forcedCollapsed]);

  // Open ADVANCED automatically when one of its destinations is active.
  useEffect(() => {
    const f = featureForPage(currentPage);
    if (f?.section === "advanced") setAdvOpen(true);
  }, [currentPage]);

  // Close mobile drawer when leaving mobile.
  useEffect(() => { if (!isMobile) setMobileOpen(false); }, [isMobile]);

  // Global "/" opens the palette (ignored while typing).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "/" && !paletteOpen) {
        const tag = (document.activeElement?.tagName || "").toUpperCase();
        if (tag !== "INPUT" && tag !== "TEXTAREA") { e.preventDefault(); setPaletteOpen(true); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [paletteOpen]);

  const isAdmin = !!user?.is_admin;
  const isAgent = user?.user_type === "agent" || user?.user_type === "admin" || isAdmin;

  const go = (page: string) => { onNavigate(page); setMenuOpen(false); if (isMobile) setMobileOpen(false); };
  const openLocked = (id: FeatureId) => { setLockedModal(id); setMenuOpen(false); };
  const isActive = (f: NavFeature) =>
    currentPage === f.page || f.altPages?.includes(currentPage) || f.children?.some((c) => c.page === currentPage);

  const showTip = (e: React.MouseEvent | React.FocusEvent, text: string) => {
    if (!effectiveCollapsed) return;
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setTip({ show: true, text, top: Math.round(r.top + r.height / 2), left: Math.round(r.right + 10) });
  };
  const hideTip = () => setTip((t) => ({ ...t, show: false }));

  const manageItems = MANAGE_ITEMS.filter(
    (m) => (!m.adminOnly || isAdmin) && (!m.agentOnly || isAgent),
  );

  // Features shown in the rail for this section. "hidden" features are dropped
  // entirely; "live" and "locked" both render ("locked" shows a lock icon).
  const visibleFeatures = (sectionId: string) =>
    FEATURES.filter((f) => f.section === sectionId && stateOf(states, f.id) !== "hidden");

  // ── Expanded list row ─────────────────────────────────────────────────────
  const renderItem = (f: NavFeature) => {
    const Icon = f.icon;
    const active = isActive(f);
    const locked = stateOf(states, f.id) === "locked";

    if (locked) {
      return (
        <button
          key={f.id}
          className="navlock w-full overflow-hidden"
          onClick={() => openLocked(f.id)}
          style={{ margin: "1px 0", padding: 0, border: "none", borderRadius: 9, cursor: "pointer", background: "transparent", fontFamily: "inherit", transition: "background 150ms var(--ease-standard)" }}
        >
          <span className="lrest items-center" style={{ gap: 11, padding: "8px 10px" }}>
            <span className="inline-flex items-center justify-center shrink-0 text-muted-foreground" style={{ width: 29, height: 29, borderRadius: 8, boxSizing: "border-box", border: "1px solid var(--border)", background: "var(--muted)" }}>
              <Icon style={{ width: 17, height: 17, strokeWidth: 1.75 }} />
            </span>
            <span className="flex-1 text-left text-foreground" style={{ fontSize: 13.5, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.label}</span>
            <span className="inline-flex items-center justify-center shrink-0 text-muted-foreground" style={{ width: 22, height: 22 }}>
              <Lock style={{ width: 15, height: 15, strokeWidth: 2 }} />
            </span>
          </span>
          <span className="linvite items-center" style={{ gap: 11, padding: "8px 10px" }}>
            <span className="inline-flex items-center justify-center shrink-0" style={{ width: 29, height: 29, borderRadius: 8, background: "var(--alpha-teal-10)", color: tt }}>
              <Icon style={{ width: 17, height: 17, strokeWidth: 1.75 }} />
            </span>
            <span className="flex-1 text-left" style={{ fontSize: 13.5, fontWeight: 600, color: tt }}>Preview</span>
            <span className="inline-flex shrink-0" style={{ color: tt }}><ArrowRight style={{ width: 15, height: 15, strokeWidth: 1.75 }} /></span>
          </span>
        </button>
      );
    }

    const showBadge = f.badge === "runCount" && runCount > 0;
    return (
      <div key={f.id}>
        <button
          onClick={() => go(f.page)}
          aria-current={active ? "page" : undefined}
          className="relative w-full flex items-center text-left"
          style={{
            gap: 11, padding: "8px 10px", margin: "1px 0", border: "none", borderRadius: 9, cursor: "pointer",
            background: active ? "var(--alpha-teal-10)" : "transparent", color: "var(--foreground)",
            fontFamily: "inherit", fontSize: 13.5, fontWeight: active ? 600 : 500,
            transition: "background 150ms var(--ease-standard)",
          }}
        >
          <span aria-hidden style={{ position: "absolute", left: -2, top: "50%", transform: "translateY(-50%)", width: 3, height: 20, borderRadius: "0 3px 3px 0", background: active ? "var(--alpha-teal)" : "transparent" }} />
          <span className="inline-flex items-center justify-center shrink-0" style={{ width: 29, height: 29, borderRadius: 8, boxSizing: "border-box", border: `1px solid ${active ? "var(--alpha-teal)" : "var(--border)"}`, background: active ? "var(--alpha-teal)" : "var(--muted)", color: active ? "#04312F" : "var(--muted-foreground)" }}>
            <Icon style={{ width: 17, height: 17, strokeWidth: 1.75 }} />
          </span>
          <span className="flex-1" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.label}</span>
          {showBadge && (
            <span className="inline-flex items-center justify-center shrink-0" style={{ minWidth: 19, height: 19, padding: "0 5px", borderRadius: 999, background: "var(--infrared-coral)", color: "#fff", fontSize: 10.5, fontWeight: 700 }}>{runCount}</span>
          )}
        </button>
        {/* Nested histories under their active parent. */}
        {active && f.children?.map((c) => {
          const CIcon = c.icon;
          const cActive = currentPage === c.page;
          return (
            <button
              key={c.page}
              onClick={() => go(c.page)}
              className="w-full flex items-center text-left"
              style={{
                gap: 10, padding: "7px 10px 7px 12px", margin: "1px 0 1px 19px", border: "none",
                borderLeft: "1px solid var(--border)", borderRadius: 8, cursor: "pointer", fontFamily: "inherit",
                fontSize: 12.5, fontWeight: cActive ? 600 : 500,
                background: cActive ? "var(--alpha-teal-10)" : "transparent",
                color: cActive ? tt : "var(--muted-foreground)",
              }}
            >
              <span className="inline-flex shrink-0" style={{ color: cActive ? tt : "var(--muted-foreground)" }}><CIcon style={{ width: 14, height: 14, strokeWidth: 1.75 }} /></span>
              <span className="flex-1" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.label}</span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderSection = (sectionId: string) => {
    const def = SECTIONS.find((s) => s.id === sectionId)!;
    const items = visibleFeatures(sectionId);
    if (!items.length) return null;
    const collapsibleClosed = def.collapsible && !advOpen;
    return (
      <div key={sectionId}>
        <div
          onClick={def.collapsible ? () => setAdvOpen((v) => !v) : undefined}
          className="flex items-center"
          style={{ gap: 6, padding: "14px 10px 6px", cursor: def.collapsible ? "pointer" : "default" }}
        >
          <span className="text-muted-foreground" style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" }}>{def.label}</span>
          <span style={{ flex: 1 }} />
          {def.collapsible && (
            <span className="inline-flex text-muted-foreground" style={{ transform: advOpen ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 160ms var(--ease-standard)" }}>
              <ChevronRight style={{ width: 14, height: 14, strokeWidth: 1.75 }} />
            </span>
          )}
        </div>
        {!collapsibleClosed && items.map(renderItem)}
      </div>
    );
  };

  // ── Collapsed icon cell ───────────────────────────────────────────────────
  const renderIconCell = (f: NavFeature) => {
    const Icon = f.icon;
    const active = isActive(f);
    const locked = stateOf(states, f.id) === "locked";
    const showBadge = f.badge === "runCount" && runCount > 0;
    return (
      <button
        key={f.id}
        aria-label={f.label}
        aria-current={active ? "page" : undefined}
        onClick={() => (locked ? openLocked(f.id) : go(f.page))}
        onMouseEnter={(e) => showTip(e, f.label)}
        onMouseLeave={hideTip}
        onFocus={(e) => showTip(e, f.label)}
        onBlur={hideTip}
        className="relative inline-flex items-center justify-center"
        style={{ width: 46, height: 40, border: "none", borderRadius: 10, background: active ? "var(--alpha-teal-10)" : "transparent", cursor: "pointer", transition: "background 150ms var(--ease-standard)" }}
      >
        <span aria-hidden style={{ position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)", width: 3, height: 18, borderRadius: "0 3px 3px 0", background: active ? "var(--alpha-teal)" : "transparent" }} />
        <span className="inline-flex items-center justify-center" style={{ width: 32, height: 32, borderRadius: 8, boxSizing: "border-box", border: `1px solid ${active ? "var(--alpha-teal)" : "var(--border)"}`, background: active ? "var(--alpha-teal)" : "var(--muted)", color: active ? "#04312F" : "var(--muted-foreground)" }}>
          <Icon style={{ width: 17, height: 17, strokeWidth: 1.75 }} />
        </span>
        {locked && (
          <span className="inline-flex items-center justify-center bg-card text-muted-foreground" style={{ position: "absolute", bottom: 2, right: 6, width: 14, height: 14, borderRadius: "50%" }}>
            <Lock style={{ width: 9, height: 9, strokeWidth: 2.4 }} />
          </span>
        )}
        {showBadge && (
          <span style={{ position: "absolute", top: 3, right: 7, width: 8, height: 8, borderRadius: "50%", background: "var(--infrared-coral)", border: "1.5px solid var(--background)" }} />
        )}
      </button>
    );
  };

  // ── Identity / workspace menu (expanded) ──────────────────────────────────
  const displayName = user?.full_name?.trim() || user?.username?.split("@")[0] || "";
  const roleLabel = isAdmin ? "Admin" : isAgent ? "Agent user" : "Member";

  const Identity = (
    <div className="shrink-0" style={{ padding: "18px 16px 12px", position: "relative", zIndex: 50 }}>
      <div className="flex items-center" style={{ gap: 7, padding: "0 4px 14px" }}>
        <span className="text-foreground" style={{ fontSize: 21, fontWeight: 800, letterSpacing: "-0.035em" }}>Upmode</span>
      </div>
      <div style={{ position: "relative" }}>
        <button
          onClick={() => setMenuOpen((v) => !v)}
          className="w-full flex items-center bg-card"
          style={{ gap: 10, padding: "9px 10px", borderRadius: 10, border: "1px solid var(--border)", cursor: "pointer", fontFamily: "inherit" }}
        >
          <span className="inline-flex items-center justify-center shrink-0" style={{ width: 32, height: 32, borderRadius: 8, background: BRAND_CHIP, color: "#fff", fontWeight: 700, fontSize: 15 }}>{brandMonogram(activeBrandName)}</span>
          <span className="flex-1 text-left min-w-0">
            <span className="block text-foreground" style={{ fontSize: 13.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{activeBrandName || "No analysis"}</span>
            <span className="flex items-center text-muted-foreground" style={{ gap: 5, fontSize: 11.5, marginTop: 1 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--signal-green)", display: "inline-block" }} />Active analysis
            </span>
          </span>
          <span className="inline-flex shrink-0 text-muted-foreground" style={{ transform: menuOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 160ms var(--ease-standard)" }}><ChevronDown style={{ width: 16, height: 16, strokeWidth: 1.75 }} /></span>
        </button>

        {menuOpen && (
          <>
            <div onClick={() => setMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 39 }} />
            <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 40, background: "var(--popover)", border: "1px solid var(--border)", borderRadius: 12, boxShadow: "var(--elevation-lg)", padding: 6 }}>
              <div className="text-muted-foreground" style={{ padding: "8px 8px 5px", fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase" }}>Switch analysis</div>
              <button onClick={() => { setMenuOpen(false); onOpenBrandSwitcher?.(); }} className="w-full flex items-center" style={{ gap: 10, padding: "7px 8px", border: "none", borderRadius: 8, background: "transparent", cursor: "pointer", fontFamily: "inherit" }}>
                <span className="inline-flex items-center justify-center shrink-0" style={{ width: 28, height: 28, borderRadius: 7, background: BRAND_CHIP, color: "#fff", fontWeight: 700, fontSize: 13 }}>{brandMonogram(activeBrandName)}</span>
                <span className="flex-1 text-left min-w-0"><span className="block text-foreground" style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{activeBrandName || "No analysis"}</span><span className="block text-muted-foreground" style={{ fontSize: 11 }}>Switch or browse all</span></span>
                <span className="inline-flex shrink-0" style={{ color: tt }}><Check style={{ width: 15, height: 15, strokeWidth: 2 }} /></span>
              </button>
              <button onClick={() => go("brand-search")} className="w-full flex items-center text-muted-foreground" style={{ gap: 10, padding: 8, marginTop: 2, border: "none", borderRadius: 8, background: "transparent", cursor: "pointer", fontFamily: "inherit" }}>
                <span className="inline-flex items-center justify-center shrink-0" style={{ width: 28, height: 28, borderRadius: 7, border: "1px dashed var(--border)" }}><Plus style={{ width: 15, height: 15, strokeWidth: 1.75 }} /></span>
                <span style={{ fontSize: 13, fontWeight: 500 }}>New analysis</span>
              </button>
              <div style={{ height: 1, background: "var(--border)", margin: "6px 4px" }} />
              <div className="flex items-center" style={{ gap: 10, padding: "6px 8px" }}>
                <span className="inline-flex items-center justify-center shrink-0 text-muted-foreground" style={{ width: 30, height: 30, borderRadius: "50%", background: "var(--muted)", fontWeight: 600, fontSize: 11 }}>{brandInitials(displayName)}</span>
                <span className="flex-1 min-w-0"><span className="block text-foreground" style={{ fontSize: 13, fontWeight: 600 }}>{displayName || "Account"}</span><span className="block text-muted-foreground" style={{ fontSize: 11 }}>{roleLabel}</span></span>
              </div>
              <button onClick={() => go("settings")} className="w-full flex items-center text-foreground" style={{ gap: 10, padding: 8, border: "none", borderRadius: 8, background: "transparent", cursor: "pointer", fontFamily: "inherit", fontSize: 13 }}><Settings style={{ width: 15, height: 15, strokeWidth: 1.75 }} /> Settings</button>
              <button onClick={() => { setMenuOpen(false); onLogout(); }} className="w-full flex items-center text-foreground" style={{ gap: 10, padding: 8, border: "none", borderRadius: 8, background: "transparent", cursor: "pointer", fontFamily: "inherit", fontSize: 13 }}><LogOut style={{ width: 15, height: 15, strokeWidth: 1.75 }} /> Sign out</button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  // ── Expanded body ─────────────────────────────────────────────────────────
  const ExpandedBody = (
    <div className="flex flex-col h-full">
      {Identity}
      <div className="shrink-0 flex" style={{ padding: "0 16px 12px", gap: 8 }}>
        <button onClick={() => setPaletteOpen(true)} className="flex-1 flex items-center bg-card text-muted-foreground" style={{ gap: 9, padding: "9px 11px", borderRadius: 9, border: "1px solid var(--border)", cursor: "pointer", fontFamily: "inherit" }}>
          <Search style={{ width: 16, height: 16, strokeWidth: 1.75 }} /><span className="flex-1 text-left" style={{ fontSize: 13 }}>Search…</span>
          <span className="font-mono" style={{ fontSize: 10, border: "1px solid var(--border)", borderRadius: 4, padding: "1px 5px" }}>/</span>
        </button>
        <button aria-label="New analysis" onClick={() => go("brand-search")} className="inline-flex items-center justify-center bg-card text-muted-foreground shrink-0" style={{ width: 38, borderRadius: 9, border: "1px solid var(--border)", cursor: "pointer" }}><Plus style={{ width: 17, height: 17, strokeWidth: 1.75 }} /></button>
      </div>
      <div style={{ height: 1, background: "var(--border)", margin: "0 16px" }} />
      <nav role="navigation" aria-label="Primary" className="nav-scroll" style={{ flex: 1, overflowY: "auto", padding: "8px 12px 10px" }}>
        {SECTIONS.map((s) => renderSection(s.id))}
      </nav>
      <div className="shrink-0" style={{ borderTop: "1px solid var(--border)", padding: "8px 16px 12px" }}>
        <button onClick={() => setManageOpen((v) => !v)} className="w-full flex items-center text-muted-foreground" style={{ gap: 8, padding: "8px 8px", border: "none", borderRadius: 8, background: "transparent", cursor: "pointer", fontFamily: "inherit" }}>
          <Settings style={{ width: 16, height: 16, strokeWidth: 1.75 }} />
          <span className="flex-1 text-left" style={{ fontSize: 12.5, fontWeight: 600, letterSpacing: "0.04em" }}>Manage</span>
          <span className="inline-flex" style={{ transform: manageOpen ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 160ms var(--ease-standard)" }}><ChevronRight style={{ width: 14, height: 14, strokeWidth: 1.75 }} /></span>
        </button>
        {manageOpen && (
          <div className="flex flex-col" style={{ gap: 1, marginTop: 2 }}>
            {manageItems.map((m) => {
              const MIcon = m.icon;
              return (
                <button key={m.page} onClick={() => go(m.page)} className="w-full flex items-center text-foreground" style={{ gap: 10, padding: "7px 10px", border: "none", borderRadius: 8, background: currentPage === m.page ? "var(--muted)" : "transparent", cursor: "pointer", fontFamily: "inherit", fontSize: 12.5 }}>
                  <span className="inline-flex shrink-0 text-muted-foreground"><MIcon style={{ width: 15, height: 15, strokeWidth: 1.75 }} /></span>{m.label}
                </button>
              );
            })}
          </div>
        )}
        <div className="flex items-center" style={{ gap: 6, marginTop: 2 }}>
          <button onClick={() => setCollapsed(true)} className="flex-1 flex items-center text-muted-foreground" style={{ gap: 10, padding: "8px 8px", border: "none", borderRadius: 8, background: "transparent", cursor: "pointer", fontFamily: "inherit" }}>
            <ChevronsLeft style={{ width: 16, height: 16, strokeWidth: 1.75 }} /><span style={{ fontSize: 12.5 }}>Collapse</span>
          </button>
          <div className="flex items-center bg-card" style={{ gap: 2, padding: 2, border: "1px solid var(--border)", borderRadius: 8 }}>
            <button onClick={() => setTheme("light")} aria-label="Light" className="inline-flex items-center justify-center" style={{ width: 26, height: 24, border: "none", borderRadius: 6, background: theme === "light" ? "var(--alpha-teal-10)" : "transparent", color: theme === "light" ? tt : "var(--muted-foreground)", cursor: "pointer" }}><Sun style={{ width: 15, height: 15, strokeWidth: 1.75 }} /></button>
            <button onClick={() => setTheme("dark")} aria-label="Dark" className="inline-flex items-center justify-center" style={{ width: 26, height: 24, border: "none", borderRadius: 6, background: theme === "dark" ? "var(--alpha-teal-10)" : "transparent", color: theme === "dark" ? tt : "var(--muted-foreground)", cursor: "pointer" }}><Moon style={{ width: 14, height: 14, strokeWidth: 1.75 }} /></button>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Collapsed body ────────────────────────────────────────────────────────
  const CollapsedBody = (
    <div className="flex flex-col h-full">
      <div className="shrink-0 flex flex-col items-center" style={{ padding: "16px 0 10px", gap: 11 }}>
        <span className="text-foreground" style={{ fontSize: 20, fontWeight: 800, letterSpacing: "-0.035em" }}>Up</span>
        <button onClick={() => setCollapsed(false)} aria-label={`${activeBrandName || "Workspace"} · expand`} onMouseEnter={(e) => showTip(e, activeBrandName || "Workspace")} onMouseLeave={hideTip} onFocus={(e) => showTip(e, activeBrandName || "Workspace")} onBlur={hideTip} className="relative inline-flex items-center justify-center" style={{ width: 38, height: 38, borderRadius: 9, background: BRAND_CHIP, color: "#fff", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 17 }}>
          {brandMonogram(activeBrandName)}
          <span style={{ position: "absolute", top: -2, right: -2, width: 10, height: 10, borderRadius: "50%", background: "var(--signal-green)", border: "2px solid var(--background)" }} />
        </button>
      </div>
      <div className="shrink-0 flex flex-col items-center" style={{ gap: 8, padding: "0 0 10px" }}>
        <button onClick={() => setPaletteOpen(true)} aria-label="Search" onMouseEnter={(e) => showTip(e, "Search  ·  /")} onMouseLeave={hideTip} onFocus={(e) => showTip(e, "Search  ·  /")} onBlur={hideTip} className="inline-flex items-center justify-center bg-card text-muted-foreground" style={{ width: 38, height: 38, borderRadius: 9, border: "1px solid var(--border)", cursor: "pointer" }}><Search style={{ width: 18, height: 18, strokeWidth: 1.75 }} /></button>
        <button onClick={() => go("brand-search")} aria-label="New analysis" onMouseEnter={(e) => showTip(e, "New analysis")} onMouseLeave={hideTip} onFocus={(e) => showTip(e, "New analysis")} onBlur={hideTip} className="inline-flex items-center justify-center bg-card text-muted-foreground" style={{ width: 38, height: 38, borderRadius: 9, border: "1px solid var(--border)", cursor: "pointer" }}><Plus style={{ width: 18, height: 18, strokeWidth: 1.75 }} /></button>
      </div>
      <div style={{ height: 1, background: "var(--border)", margin: "0 18px" }} />
      <nav role="navigation" aria-label="Primary" className="nav-scroll flex flex-col items-center" style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: "10px 0", gap: 2 }}>
        {SECTIONS.filter((s) => visibleFeatures(s.id).length > 0).map((s, si) => (
          <div key={s.id} className="flex flex-col items-center" style={{ gap: 2, width: "100%" }}>
            {si > 0 && <div style={{ width: 30, height: 1, background: "var(--border)", margin: "8px 0" }} />}
            {visibleFeatures(s.id).map(renderIconCell)}
          </div>
        ))}
      </nav>
      <div className="shrink-0 flex flex-col items-center" style={{ borderTop: "1px solid var(--border)", padding: "10px 0", gap: 8 }}>
        <button onClick={() => setTheme(theme === "dark" ? "light" : "dark")} aria-label="Toggle theme" onMouseEnter={(e) => showTip(e, "Toggle theme")} onMouseLeave={hideTip} onFocus={(e) => showTip(e, "Toggle theme")} onBlur={hideTip} className="inline-flex items-center justify-center bg-card text-muted-foreground" style={{ width: 38, height: 36, borderRadius: 9, border: "1px solid var(--border)", cursor: "pointer" }}>{theme === "dark" ? <Sun style={{ width: 17, height: 17, strokeWidth: 1.75 }} /> : <Moon style={{ width: 17, height: 17, strokeWidth: 1.75 }} />}</button>
        <button onClick={() => setCollapsed(false)} aria-label="Expand" onMouseEnter={(e) => showTip(e, "Expand")} onMouseLeave={hideTip} onFocus={(e) => showTip(e, "Expand")} onBlur={hideTip} className="inline-flex items-center justify-center text-muted-foreground" style={{ width: 38, height: 36, borderRadius: 9, border: "none", background: "transparent", cursor: "pointer" }}><ChevronsRight style={{ width: 18, height: 18, strokeWidth: 1.75 }} /></button>
      </div>
    </div>
  );

  const railWidth = isMobile ? "min(288px, 85vw)" : (collapsed ? 80 : 288);
  const railTransform = isMobile ? (mobileOpen ? "translateX(0)" : "translateX(-110%)") : "translateX(0)";

  return (
    <>
      {isMobile && (
        <button type="button" onClick={() => setMobileOpen((v) => !v)} aria-label={mobileOpen ? "Close navigation" : "Open navigation"} aria-expanded={mobileOpen} className="fixed flex items-center justify-center bg-card" style={{ top: 12, left: 12, width: 44, height: 44, zIndex: 50, borderRadius: 12, border: "1px solid var(--border)", boxShadow: "var(--elevation-md)", cursor: "pointer" }}>
          {mobileOpen ? <X className="text-foreground" style={{ width: 20, height: 20 }} /> : <Menu className="text-foreground" style={{ width: 20, height: 20 }} />}
        </button>
      )}
      {isMobile && mobileOpen && (
        <div onClick={() => setMobileOpen(false)} aria-hidden className="fixed inset-0" style={{ zIndex: 45, background: "rgba(0,0,0,0.45)" }} />
      )}

      <div
        className="app-nav fixed left-0 top-0 h-screen border-r border-border bg-background"
        style={{ width: railWidth, transform: railTransform, zIndex: isMobile ? 46 : 30, boxShadow: "var(--elevation-sm)", transition: "width 300ms var(--ease-standard), transform 300ms var(--ease-standard)", overflow: "hidden" }}
      >
        {effectiveCollapsed ? CollapsedBody : ExpandedBody}
      </div>

      {/* Portal-style tooltip for the collapsed rail (fixed → escapes overflow). */}
      {tip.show && effectiveCollapsed && !isMobile && (
        <div style={{ position: "fixed", left: tip.left, top: tip.top, transform: "translateY(-50%)", zIndex: 90, background: "#16181D", color: "#fff", fontSize: 12, fontWeight: 500, padding: "6px 10px", borderRadius: 7, whiteSpace: "nowrap", pointerEvents: "none", boxShadow: "0 6px 18px rgba(0,0,0,0.22)" }}>{tip.text}</div>
      )}

      <CommandPalette open={paletteOpen} states={states} onClose={() => setPaletteOpen(false)} onNavigate={go} onLocked={(id) => { setPaletteOpen(false); openLocked(id); }} />
      <LockedFeatureModal feature={lockedModal} onClose={() => setLockedModal(null)} />
    </>
  );
}
